// Screen-space scenery mask: shade only the covered pixels of an upright entity.
// This is a stylised projection, not a 3D character-height/occlusion simulation.
import { Filter, GlProgram, Texture } from 'pixi.js';

export function makeShadowFilter(): Filter {
  return new Filter({
    resolution: 1, antialias: false, padding: 0,
    glProgram: GlProgram.from({ name: 'scenery-shadow', vertex: `
      in vec2 aPosition;
      uniform vec4 uInputSize;
      uniform vec4 uOutputFrame;
      uniform vec4 uOutputTexture;
      out vec2 vTextureCoord;
      out vec2 vScreenPosition;
      void main() {
        vec2 position = aPosition * uOutputFrame.zw + uOutputFrame.xy;
        vScreenPosition = position;
        position.x = position.x * (2.0 / uOutputTexture.x) - 1.0;
        position.y = position.y * (2.0 * uOutputTexture.z / uOutputTexture.y) - uOutputTexture.z;
        gl_Position = vec4(position, 0.0, 1.0);
        vTextureCoord = aPosition * (uOutputFrame.zw * uInputSize.zw);
      }
    `, fragment: `
      in vec2 vTextureCoord;
      in vec2 vScreenPosition;
      uniform sampler2D uTexture;
      uniform sampler2D uShadowMask;
      uniform vec2 uMaskSize;
      uniform vec2 uMaskOrigin;
      uniform vec3 uShadowColor;
      out vec4 finalColor;
      void main() {
        vec4 color = texture(uTexture, vTextureCoord);
        vec2 uv = (vScreenPosition - uMaskOrigin) / uMaskSize;
        float shade = 0.0;
        if (uv.x >= 0.0 && uv.y >= 0.0 && uv.x < 1.0 && uv.y < 1.0)
          shade = texture(uShadowMask, uv).a;
        // As on the ground: the shadow colour laid over at the mask's strength (colour is premultiplied by alpha).
        finalColor = vec4(color.rgb * (1.0 - shade) + uShadowColor * shade * color.a, color.a);
      }
    ` }),
    resources: {
      uShadowMask: Texture.EMPTY.source,
      shadowUniforms: {
        uMaskSize: { value: [1, 1], type: 'vec2<f32>' },
        uMaskOrigin: { value: [0, 0], type: 'vec2<f32>' },
        uShadowColor: { value: [0, 0, 0], type: 'vec3<f32>' },
      },
    },
  });
}
